import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useResponsive } from '@/hooks/useResponsive'
import { api, baseURL } from '@/api/axios'
import { pdfService } from '@/services/pdfService'
import { storageService } from '@/services/storageService'
import { Button } from '@/components/Button'
import { BottomNavigator } from '@/components/BottomNavigator'
import { colors, spacing } from '@/theme'
import {
  FileText,
  Edit,
  Download,
  LogOut,
  Sparkles,
  TrendingUp,
  Award,
  Briefcase,
  GraduationCap,
  Star,
  Lightbulb,
  ArrowRight,
  Eye,
  Share2,
  Copy,
  Users,
  Camera,
  X,
  QrCode,
} from 'lucide-react'
import { CVData } from '@/types'
import { useShare } from '@/hooks/useShare'
import { WorkerInfoResponse, workerInfoService } from '@/api/wizard/workerInfo.service'
import { useWorkerInfo } from '@/hooks/useWorkerInfo'
import { WorkerInfo } from '@/types/workerInfo.types'
import { workerService } from '@/api/workers/worker.service'

export function DashboardScreen() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { maxWidth, isDesktop, isTablet } = useResponsive()
  const [cvData, setCvData] = useState<WorkerInfo | null>(null)
  const [completionPercentage, setCompletionPercentage] = useState(0)
  const [showTip, setShowTip] = useState(true)
  const [showCamera, setShowCamera] = useState(false)
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
  const [updatingProfile, setUpdatingProfile] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
  const firstName = user?.firstName || fullName.split(' ')[0] || 'Usuario'
  const referralCode = user?.referralCode || (user?.email ? `WD-${user.email.split('@')[0].toUpperCase()}` : 'WD-USER')
  const qrLink = `${window.location.origin}/register?ref=${encodeURIComponent(referralCode)}`
  const profileImageUrl = (() => {
    if (!user?.profileImage) return ''
    if (user.profileImage.startsWith('http://') || user.profileImage.startsWith('https://')) {
      return user.profileImage
    }
    const baseURL = api.defaults.baseURL || ''
    const apiRoot = baseURL.replace(/\/api\/v1\/?$/, '')
    const path = user.profileImage.startsWith('/') ? user.profileImage : `/${user.profileImage}`
    return `${apiRoot}${path}`
  })()
  console.log(user)
  const [isAvailable, setIsAvailable] = useState(user?.isAvilable || false);
  const { share } = useShare();
  const { categories, activeCategory, changeCategory, getCategoryProgress, loading, updateAnswer, saving, workerInfo } = useWorkerInfo(user?.id || '');
  useEffect(()=>{
    if(!workerInfo) return;
    const totalProgress = categories.length > 0
    ? Math.round(categories.reduce((acc, cat) => acc + getCategoryProgress(cat.category), 0) / categories.length)
    : 0;
    
    setCompletionPercentage(totalProgress);
    setCvData(workerInfo);
  }, [workerInfo])
  const handleGeneratePDF = () => {
    if (!cvData) {
      alert('Primero completa tu información en el wizard')
      navigate('/wizard')
      return
    }

    try {
      window.open(`${baseURL}/workers/${user?.id}/cv/download`, '_blank')
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Error al generar el PDF. Por favor, intenta de nuevo.')
    }
  }

  const handleEdit = () => {
    navigate('/wizard')
  }
  const handleIsAvailable = () => {
    const updatedUser = { ...user, isAvilable: !isAvailable }
    setIsAvailable(!isAvailable)
    workerService.updateWorker(user?.id || '', updatedUser)

  }

  const handleLogout = () => {
    logout()
  }

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      })
      setCameraStream(stream)
      setShowCamera(true)
    } catch (error) {
      console.error('Error accessing camera:', error)
      alert('No se pudo acceder a la cámara. Asegúrate de dar permisos.')
    }
  }

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop())
      setCameraStream(null)
    }
    setShowCamera(false)
  }

  const takePhoto = async () => {
    if (!cameraStream) return

    const video = document.querySelector('video') as HTMLVideoElement
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!video || !ctx) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    ctx.drawImage(video, 0, 0)

    const imageData = canvas.toDataURL('image/jpeg', 0.8)

    try {
      setUpdatingProfile(true)
      const response = await api.put('/auth/profile/image', {
        image: imageData,
        userId: user?.id
      })

      if (response.status === 200) {
        // Actualizar la imagen en el estado local
        const updatedUser = { ...user, profileImage: response.data.profileImage }
        // Aquí deberías actualizar el contexto de auth si es necesario
        localStorage.setItem("user", JSON.stringify(updatedUser))
        window.location.reload() // Recargar para mostrar la nueva imagen
      }
    } catch (error) {
      console.error('Error updating profile image:', error)
      alert('Error al actualizar la imagen de perfil')
    } finally {
      setUpdatingProfile(false)
      closeCamera()
    }
  }


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  }
  const handleShare = () => {
    const url = `${window.location.origin}/register?ref=${encodeURIComponent(referralCode)}`
    share({
      title: "Mira este sitio 🚀",
      text: "Te recomiendo visitar:",
      url: url,
    });
  };
  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: colors.primary[900],
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(37, 99, 235, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(30, 41, 59, 0.1) 0%, transparent 50%)`,
          padding: isDesktop ? spacing[6] : isTablet ? spacing[5] : spacing[4],
          paddingBottom: '56px',
        }}
      >
        <div style={{ maxWidth: maxWidth, margin: '0 auto' }}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[4],
            }}
          >
            {/* Header mejorado */}
<motion.div
  variants={itemVariants}
  style={{
    background: 'rgba(15, 23, 42, 0.78)',
    backdropFilter: 'blur(18px)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '28px',
    padding: spacing[6],
    color: colors.white,
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
    position: 'relative',
    overflow: 'hidden',
  }}
>
  {/* Glow elegante */}
  <div
    style={{
      position: 'absolute',
      top: -120,
      right: -120,
      width: 260,
      height: 260,
      borderRadius: '50%',
      background:
        'radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0) 70%)',
      pointerEvents: 'none',
    }}
  />

  {/* TOP */}
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: spacing[4],
      position: 'relative',
      zIndex: 2,
    }}
  >
    {/* LEFT */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
        flex: 1,
      }}
    >
      {/* AVATAR + QR */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <motion.div
          whileHover={{ scale: 1.03 }}
          transition={{ type: 'spring', stiffness: 300 }}
          style={{
            position: 'relative',
          }}
        >
          <img
            src={profileImageUrl || '/src/assets/perfil.jpeg'}
            alt={fullName || 'Perfil'}
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              objectFit: 'cover',
              border: '2px solid rgba(255,255,255,0.12)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            }}
          />

          {/* EDIT PHOTO */}
          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={openCamera}
            style={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '2px solid rgba(15,23,42,1)',
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: colors.white,
            }}
          >
            <Camera size={14} />
          </motion.button>

          {/* STATUS DOT */}
          <div
            style={{
              position: 'absolute',
              bottom: 4,
              left: 4,
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: isAvailable ? '#22c55e' : '#64748b',
              border: '2px solid #0f172a',
            }}
          />
        </motion.div>
        
        {/* QR ICON */}
        <motion.div
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowQr(true)}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // color: 'rgba(255,255,255,0.5)',
            color: 'rgba(255, 255, 255, 0.88)',
          }}
          title="Código QR de referido"
        >
          <QrCode size={48} />
        </motion.div>
      </div>

      {/* INFO */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {/* MESSAGE */}
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
          >
            Hola, {firstName} 👋
          </h2>

          <p
            style={{
              margin: '6px 0 0 0',
              fontSize: '14px',
              lineHeight: 1.5,
              color: 'rgba(255,255,255,0.72)',
              maxWidth: '500px',
            }}
          >
            Eres parte del equipo{' '}
            <span
              style={{
                color: '#60a5fa',
                fontWeight: 700,
              }}
            >
              WINGDRIVE,
            </span>
            eres valioso para nosotros.
          </p>
        </div>

        {/* STATUS */}
        <motion.div
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsAvailable(!isAvailable)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: 'fit-content',
            padding: '8px 14px',
            borderRadius: '999px',
            background: isAvailable
              ? 'rgba(34,197,94,0.12)'
              : 'rgba(255,255,255,0.06)',
            border: isAvailable
              ? '1px solid rgba(34,197,94,0.25)'
              : '1px solid rgba(255,255,255,0.08)',
            cursor: 'pointer',
            transition: 'all .25s ease',
          }}
        >
          {/* SWITCH */}
          <div
            style={{
              width: 42,
              height: 22,
              borderRadius: 999,
              padding: 2,
              background: isAvailable
                ? 'linear-gradient(135deg,#22c55e,#16a34a)'
                : 'rgba(255,255,255,0.18)',
              display: 'flex',
              justifyContent: isAvailable ? 'flex-end' : 'flex-start',
              alignItems: 'center',
              transition: 'all .25s ease',
            }}
            onClick={handleIsAvailable}
          >
            <motion.div
              layout
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#fff',
              }}
            />
          </div>

          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.92)',
            }}
          >
            {isAvailable
              ? 'Disponible para trabajar'
              : 'No disponible'}
          </span>
        </motion.div>

        {/* EMAIL */}
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            color: 'rgba(255,255,255,0.5)',
          }}
        >
          {user?.email}
        </p>
      </div>
    </div>
  </div>

  {/* PROGRESS */}
  {cvData && (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      style={{
        marginTop: spacing[6],
        position: 'relative',
        zIndex: 2,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.8)',
          }}
        >
          Perfil completado
        </span>

        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#60a5fa',
          }}
        >
          {completionPercentage}%
        </span>
      </div>

      <div
        style={{
          width: '100%',
          height: 10,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.08)',
          overflow: 'hidden',
        }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${completionPercentage}%` }}
          transition={{
            duration: 1,
            ease: 'easeOut',
          }}
          style={{
            height: '100%',
            borderRadius: 999,
            background:
              'linear-gradient(90deg,#3b82f6,#60a5fa)',
            boxShadow: '0 0 20px rgba(96,165,250,0.5)',
          }}
        />
      </div>
    </motion.div>
  )}
</motion.div>

            {/* Stats Cards */}
            {/* {cvData && (
              <motion.div
                variants={itemVariants}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
                  gap: spacing[3],
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: '16px',
                    padding: spacing[4],
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                    border: `1.5px solid ${colors.primary[200]}`,
                    background: `linear-gradient(135deg, ${colors.white} 0%, rgba(30, 99, 235, 0.05) 100%)`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        backgroundColor: colors.primary[100],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Briefcase size={20} color={colors.primary[500]} />
                    </div>
                    <div>
                      <p style={{ fontSize: '24px', fontWeight: 700, color: colors.gray[900] }}>
                        {stats.experiences}
                      </p>
                      <p style={{ fontSize: '12px', color: colors.gray[600] }}>
                        Experiencias
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: '16px',
                    padding: spacing[4],
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                    border: `1.5px solid ${colors.primary[200]}`,
                    background: `linear-gradient(135deg, ${colors.white} 0%, rgba(30, 99, 235, 0.05) 100%)`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        backgroundColor: `${colors.primary[500]}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <GraduationCap size={20} color={colors.primary[500]} />
                    </div>
                    <div>
                      <p style={{ fontSize: '24px', fontWeight: 700, color: colors.gray[900] }}>
                        {stats.educations}
                      </p>
                      <p style={{ fontSize: '12px', color: colors.gray[600] }}>
                        Educación
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: '16px',
                    padding: spacing[4],
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                    border: `1.5px solid ${colors.primary[200]}`,
                    background: `linear-gradient(135deg, ${colors.white} 0%, rgba(30, 99, 235, 0.05) 100%)`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        backgroundColor: colors.success[50],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Star size={20} color={colors.success[500]} />
                    </div>
                    <div>
                      <p style={{ fontSize: '24px', fontWeight: 700, color: colors.gray[900] }}>
                        {stats.skills}
                      </p>
                      <p style={{ fontSize: '12px', color: colors.gray[600] }}>
                        Habilidades
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: '16px',
                    padding: spacing[4],
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                    border: `1.5px solid ${colors.primary[200]}`,
                    background: `linear-gradient(135deg, ${colors.white} 0%, rgba(30, 99, 235, 0.05) 100%)`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        backgroundColor: colors.warning[50],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Award size={20} color={colors.warning[500]} />
                    </div>
                    <div>
                      <p style={{ fontSize: '24px', fontWeight: 700, color: colors.gray[900] }}>
                        {stats.files}
                      </p>
                      <p style={{ fontSize: '12px', color: colors.gray[600] }}>
                        Archivos
                      </p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )} */}

            {/* Tip Card */}
            {/* <AnimatePresence>
              {showTip && (
                <motion.div
                  variants={itemVariants}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary[500]} 0%, #3b82f6 50%, #2563eb 100%)`,
                    borderRadius: '20px',
                    padding: spacing[5],
                    border: `2px solid ${colors.primary[400]}`,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 10px 30px rgba(37, 99, 235, 0.3)',
                  }}
                >
                  <motion.button
                    onClick={() => setShowTip(false)}
                    style={{
                      position: 'absolute',
                      top: spacing[3],
                      right: spacing[3],
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      cursor: 'pointer',
                      color: colors.white,
                      opacity: 0.8,
                    }}
                    whileHover={{ scale: 1.2, opacity: 1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ×
                  </motion.button>

                  <div style={{ display: 'flex', gap: spacing[3], alignItems: 'flex-start' }}>
                    <motion.div
                      animate={{
                        rotate: [0, 10, -10, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatDelay: 3,
                      }}
                    >
                      <Sparkles size={32} color={colors.white} />
                    </motion.div>
                    <div style={{ flex: 1, paddingRight: spacing[8] }}>
                      <h3
                        style={{
                          fontSize: '18px',
                          fontWeight: 700,
                          color: colors.white,
                          marginBottom: spacing[2],
                        }}
                      >
                        💡 Consejo Profesional
                      </h3>
                      <p
                        style={{
                          fontSize: '14px',
                          color: 'rgba(255, 255, 255, 0.9)',
                          lineHeight: 1.6,
                        }}
                      >
                        Un CV completo y actualizado aumenta tus posibilidades de conseguir trabajo en un{' '}
                        <strong>73%</strong>. Asegúrate de incluir todas tus experiencias y habilidades.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence> */}

            {/* Actions principales */}
            <motion.div
              variants={itemVariants}
              style={{
                backgroundColor: colors.white,
                borderRadius: '20px',
                padding: spacing[6],
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                border: `1.5px solid ${colors.gray[200]}`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: spacing[5],
                }}
              >
                <h2
                  style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: colors.gray[900],
                  }}
                >
                  Acciones Rápidas
                </h2>
                <motion.div
                  animate={{
                    rotate: [0, 360],
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                >
                  <TrendingUp size={24} color={colors.primary[500]} />
                </motion.div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
                <motion.div whileHover={{ x: 5 }}>
                  <Button
                    onClick={handleEdit}
                    fullWidth
                    variant="outline"
                    icon={<Edit size={20} />}
                  >
                    Editar mi hoja de vida
                    <ArrowRight size={16} />
                  </Button>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    onClick={handleGeneratePDF}
                    fullWidth
                    variant="primary"
                    icon={<Download size={20} />}
                    disabled={!cvData}
                  >
                    Descargar mi hoja de vida.pdf
                  </Button>
                </motion.div>

                {!cvData && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    style={{
                      backgroundColor: colors.primary[50],
                      borderRadius: '12px',
                      padding: spacing[4],
                      border: `1.5px solid ${colors.primary[200]}`,
                      display: 'flex',
                      gap: spacing[3],
                      alignItems: 'center',
                    }}
                  >
                    <Lightbulb size={24} color={colors.primary[500]} />
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: colors.gray[900],
                          marginBottom: spacing[1],
                        }}
                      >
                        ¡Comienza ahora!
                      </p>
                      <p
                        style={{
                          fontSize: '12px',
                          color: colors.gray[700],
                          lineHeight: 1.5,
                        }}
                      >
                        Completa las preguntas paso a paso para crear tu CV profesional
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate('/wizard')}
                      variant="primary"
                      size="sm"
                      icon={<ArrowRight size={16} />}
                    >
                      Ir
                    </Button>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Tarjetas de Compartir e Invitar */}
            <motion.div
              variants={itemVariants}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: spacing[3],
              }}
            >
              {/* Tarjeta de Compartir CV */}
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  padding: spacing[5],
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  border: `1.5px solid ${colors.primary[200]}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: spacing[3],
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  handleShare()
                }}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '16px',
                    backgroundColor: colors.primary[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Share2 size={32} color={colors.primary[500]} />
                </motion.div>
                <div>
                </div>
                <motion.div
                  style={{
                    width: '100%',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: colors.primary[50],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing[2],
                  }}
                  whileHover={{ backgroundColor: colors.primary[100] }}
                >
                  <Share2 size={16} color={colors.primary[500]} />
                  <span style={{ fontSize: '12px', color: colors.primary[600], fontWeight: 600 }}>
                    Compatir enlace
                  </span>
                </motion.div>
              </motion.div>

              {/* Tarjeta de Invitar Amigos */}
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  padding: spacing[5],
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  border: `1.5px solid ${colors.primary[200]}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: spacing[3],
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  handleShare()
                }}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '16px',
                    backgroundColor: `${colors.primary[500]}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Users size={32} color={colors.primary[500]} />
                </motion.div>
                <div>
                </div>
                <motion.div
                  style={{
                    width: '100%',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: colors.primary[50],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing[2],
                  }}
                  whileHover={{ backgroundColor: colors.primary[100] }}
                >
                  <Copy size={16} color={colors.primary[500]} />
                  <span style={{ fontSize: '12px', color: colors.primary[600], fontWeight: 600 }}>
                    Mi Código: {referralCode}
                  </span>
                </motion.div>
              </motion.div>
            </motion.div>
            {/* Guías rápidas */}
            <motion.div
              variants={itemVariants}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[3],
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: colors.white,
                  padding: `0 ${spacing[2]}`,
                }}
              >
                Guías Rápidas
              </h3>

              {[
                {
                  icon: <FileText size={20} />,
                  title: 'Tú hoja de vida es 100% gratis y puedes descargalo cuando quieras y editarlo cuando quieras.',
                  // description: 'Puedes descargarlo y editarlo cuando quieras. Los cambios se guardan automáticamente.',
                  color: colors.primary[500],
                },
                {
                  icon: <Eye size={20} />,
                  title: 'Vista previa automática',
                  description: 'Revisa tu CV antes de descargarlo. Todos los cambios son instantáneos.',
                  color: colors.primary[600],
                  onClick: () => navigate('/vista-previa'),
                },
                {
                  icon: <Share2 size={20} />,
                  title: 'Comparte tu progreso',
                  description: 'Mantén actualizado tu perfil profesional. Los reclutadores valoran CVs actualizados.',
                  color: colors.success[500],
                },
              ].map((guide, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  whileHover={{ x: 5, scale: 1.02 }}
                  onClick={guide.onClick}
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: '16px',
                    padding: spacing[4],
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    border: `1.5px solid ${colors.primary[100]}`,
                    display: 'flex',
                    gap: spacing[3],
                    alignItems: 'flex-start',
                    cursor: guide.onClick ? 'pointer' : 'default',
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      backgroundColor: `${guide.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: guide.color,
                      flexShrink: 0,
                    }}
                  >
                    {guide.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: colors.gray[900],
                        marginBottom: spacing[1],
                      }}
                    >
                      {guide.title}
                    </h4>
                    <p
                      style={{
                        fontSize: '13px',
                        color: colors.gray[600],
                        lineHeight: 1.5,
                      }}
                    >
                      {guide.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Modal de Cámara */}
      <AnimatePresence>
        {showCamera && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: spacing[4],
            }}
            onClick={closeCamera}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              style={{
                backgroundColor: colors.white,
                borderRadius: '20px',
                padding: spacing[6],
                maxWidth: '400px',
                width: '100%',
                position: 'relative',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing[4] }}>
                <h3 style={{ fontSize: '20px', fontWeight: 700, color: colors.gray[900] }}>
                  Tomar Foto
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={closeCamera}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: spacing[1],
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X size={24} color={colors.gray[500]} />
                </motion.button>
              </div>

              <div style={{ position: 'relative', marginBottom: spacing[4] }}>
                <video
                  autoPlay
                  playsInline
                  muted
                  ref={(video) => {
                    if (video && cameraStream) {
                      video.srcObject = cameraStream;
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    backgroundColor: colors.gray[100],
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '200px',
                    height: '200px',
                    border: `3px solid ${colors.white}`,
                    borderRadius: '50%',
                    boxShadow: '0 0 0 1000px rgba(0, 0, 0, 0.3)',
                    pointerEvents: 'none',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: spacing[3] }}>
                <Button
                  onClick={closeCamera}
                  variant="outline"
                  fullWidth
                  disabled={updatingProfile}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={takePhoto}
                  variant="primary"
                  fullWidth
                  disabled={updatingProfile}
                  icon={<Camera size={20} />}
                >
                  {updatingProfile ? 'Actualizando...' : 'Tomar Foto'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal QR */}
      <AnimatePresence>
        {showQr && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: spacing[4],
            }}
            onClick={() => setShowQr(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              style={{
                backgroundColor: colors.white,
                borderRadius: '24px',
                padding: spacing[8],
                maxWidth: '380px',
                width: '100%',
                position: 'relative',
                textAlign: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowQr(false)}
                style={{
                  position: 'absolute',
                  top: spacing[3],
                  right: spacing[3],
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: spacing[1],
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.gray[500],
                }}
              >
                <X size={24} />
              </motion.button>

              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: colors.gray[900],
                  marginBottom: spacing[1],
                }}
              >
                Código QR
              </h3>
              <p
                style={{
                  fontSize: '14px',
                  color: colors.gray[500],
                  marginBottom: spacing[6],
                }}
              >
                Escanea para compartir tu enlace de referido
              </p>

              <div
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  padding: spacing[4],
                  display: 'inline-block',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  border: `1px solid ${colors.gray[200]}`,
                }}
              >
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrLink)}`}
                  alt="QR de referido"
                  style={{
                    width: 220,
                    height: 220,
                    borderRadius: '8px',
                  }}
                />
              </div>

              <p
                style={{
                  fontSize: '12px',
                  color: colors.gray[400],
                  marginTop: spacing[4],
                  wordBreak: 'break-all',
                }}
              >
                {qrLink}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNavigator />
    </>
  )
}

